import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Body2, Button, Caption1, Subtitle2, makeStyles, tokens } from '@fluentui/react-components';
import { BrainSparkleRegular, Dismiss24Regular } from '@fluentui/react-icons';

import { DocumentContext, useScrollContent } from '@modules/documents';
import { EItemScreenMode, useItemScreenStore } from '@modules/items';
import { SignInContext } from '@modules/users';

import { CustomScrollbar } from '@shared/components';
import { ToastContext } from '@shared/contexts';

import {
  AI_FINDINGS_LOADING_DURATION_MS,
  AI_FINDINGS_LOADING_PROGRESS_CHECKPOINTS,
  AI_FINDINGS_LOADING_STEPS,
  AI_FINDINGS_SIDEBAR_WIDTH_PX,
} from '../constants';
import { getAiFindings, isAiFindingsVisible, resolveAiFindingScrollValue } from '../helpers';
import type { TAiFinding, TAiFindingAction } from '../types';
import MockAiFindingDetail from './MockAiFindingDetail';
import MockAiFindingSuggestionDialog from './MockAiFindingSuggestionDialog';
import MockAiFindingsList from './MockAiFindingsList';
import MockAiFindingsLoadingState from './MockAiFindingsLoadingState';

const useStyles = makeStyles({
  container: {
    width: `${AI_FINDINGS_SIDEBAR_WIDTH_PX}px`,
    minWidth: `${AI_FINDINGS_SIDEBAR_WIDTH_PX}px`,
  },
  headerMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '18px',
    lineHeight: 1,
    color: tokens.colorBrandForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  documentName?: string;
};

const getBumpyProgress = (elapsedMs: number) => {
  const elapsedRatio = Math.min(elapsedMs / AI_FINDINGS_LOADING_DURATION_MS, 1);

  for (let index = 1; index < AI_FINDINGS_LOADING_PROGRESS_CHECKPOINTS.length; index += 1) {
    const previousCheckpoint = AI_FINDINGS_LOADING_PROGRESS_CHECKPOINTS[index - 1];
    const nextCheckpoint = AI_FINDINGS_LOADING_PROGRESS_CHECKPOINTS[index];

    if (elapsedRatio <= nextCheckpoint.time) {
      const segmentRatio =
        (elapsedRatio - previousCheckpoint.time) / (nextCheckpoint.time - previousCheckpoint.time || 1);

      return previousCheckpoint.value + (nextCheckpoint.value - previousCheckpoint.value) * segmentRatio;
    }
  }

  return 1;
};

const MockAiFindingsSidebar = ({ documentName }: TProps) => {
  const classes = useStyles();
  const { organization } = useContext(SignInContext);
  const { setScreenMode } = useItemScreenStore();
  const documentContext = useContext(DocumentContext);
  const { show } = useContext(ToastContext);
  const scrollContent = useScrollContent();

  const findings = useMemo(() => getAiFindings(documentName), [documentName]);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<TAiFindingAction | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const clearFindingHighlightsRef = useRef(() => {});

  const isVisible = isAiFindingsVisible(organization.mock?.isAiEnabled, documentName);

  useEffect(() => {
    if (!isVisible) setScreenMode();
  }, [isVisible, setScreenMode]);

  useEffect(() => {
    clearFindingHighlightsRef.current = () => scrollContent({ clearHighlight: true, noFocus: true });
  }, [scrollContent]);

  useEffect(() => {
    setSelectedFindingId(null);
    setSelectedAction(undefined);
    setLoadingProgress(0);
    setActiveStepIndex(0);
    setIsLoading(true);
    clearFindingHighlightsRef.current();

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const nextProgress = getBumpyProgress(Date.now() - startedAt);
      const nextStepIndex = Math.min(
        AI_FINDINGS_LOADING_STEPS.length - 1,
        Math.floor(nextProgress * AI_FINDINGS_LOADING_STEPS.length)
      );

      setLoadingProgress(nextProgress);
      setActiveStepIndex(nextStepIndex);

      if (nextProgress >= 1) {
        window.clearInterval(intervalId);
        setLoadingProgress(1);
        setActiveStepIndex(AI_FINDINGS_LOADING_STEPS.length - 1);
        setIsLoading(false);
      }
    }, 120);

    return () => {
      window.clearInterval(intervalId);
      clearFindingHighlightsRef.current();
    };
  }, [documentName]);

  const selectedFinding = selectedFindingId ? findings.find((finding) => finding.id === selectedFindingId) : undefined;

  const clearFindingHighlights = () => scrollContent({ clearHighlight: true, noFocus: true });

  const handleCloseSidebar = () => {
    clearFindingHighlights();
    setSelectedFindingId(null);
    setSelectedAction(undefined);
    setScreenMode();
  };

  const scrollToFindingTarget = (finding: Pick<TAiFinding, 'scrollTarget' | 'highlightText' | 'highlightTexts'>) => {
    const root = documentContext?.editor?.view?.dom as HTMLElement | undefined;
    const scrollValue = root ? resolveAiFindingScrollValue(root, finding) : undefined;
    const highlightText = finding.highlightTexts?.length ? finding.highlightTexts : finding.highlightText;

    scrollContent({
      value: scrollValue,
      highlightText,
      shouldHighlight: !highlightText && Boolean(scrollValue),
      clearHighlight: true,
    });
  };

  const handleFindingSelect = (finding: TAiFinding) => {
    setSelectedFindingId(finding.id);
    scrollToFindingTarget(finding);
  };

  const handleActionClick = (action: TAiFindingAction) => {
    if (action.previewHtml) {
      setSelectedAction(action);
      return;
    }

    show(
      'info',
      `"${action.label}" is available as a review step. Workflow handoff is not connected in this workspace yet.`
    );
  };

  const handleActionAccept = (action: TAiFindingAction) => {
    setSelectedAction(undefined);
    show('info', `"${action.label}" was accepted for review. Workflow handoff is not connected in this workspace yet.`);
  };

  if (!isVisible) return null;

  return (
    <div className={`flex flex-col h-full ${classes.container}`}>
      <MockAiFindingSuggestionDialog
        action={selectedAction}
        onAccept={handleActionAccept}
        onClose={() => setSelectedAction(undefined)}
      />

      <div className="pt-3 pl-4 pb-2 pr-3">
        <div className="flex justify-between items-center mb-2">
          <div className={classes.headerMeta}>
            <div className={classes.titleRow}>
              <BrainSparkleRegular className={classes.titleIcon} />
              <Subtitle2>AI findings</Subtitle2>
            </div>
            <Body2 className={classes.subtitle}>
              {isLoading
                ? 'Thinking through the document...'
                : `${findings.length} finding${findings.length === 1 ? '' : 's'} available`}
            </Body2>
          </div>

          <Button
            appearance="subtle"
            size="small"
            aria-label="close"
            icon={<Dismiss24Regular />}
            onClick={handleCloseSidebar}
          />
        </div>
      </div>

      <CustomScrollbar className="w-full overflow-x-hidden">
        <div className="px-4 pb-4 w-full flex flex-col gap-4">
          {isLoading ? (
            <MockAiFindingsLoadingState progress={loadingProgress} activeStepIndex={activeStepIndex} />
          ) : selectedFinding ? (
            <MockAiFindingDetail
              finding={selectedFinding}
              onBack={() => {
                clearFindingHighlights();
                setSelectedFindingId(null);
              }}
              onActionClick={handleActionClick}
            />
          ) : (
            <>
              <Caption1 className="text-neutral-500">
                Review prioritized findings and jump directly to the supporting evidence in the document.
              </Caption1>
              <MockAiFindingsList findings={findings} onSelectFinding={handleFindingSelect} />
            </>
          )}
        </div>
      </CustomScrollbar>
    </div>
  );
};

export default MockAiFindingsSidebar;
